import humanizeDuration from "humanize-duration";
import xor from "lodash/xor";
import type { ProjectVersion } from "./api/projects";
import type { TicketId, TicketKey } from "./api/tickets";
import { SelectItem } from "./typeUtils";
import { capitalCase } from "change-case";

/** FIFO structure arranged by priority. Larger values come first. */
export class PriorityQueue<T> {
    values: { value: T; priority: number }[];

    constructor() {
        this.values = [];
    }

    enqueue(value: T, priority: number) {
        const index = this.values.findIndex((v) => v.priority < priority);

        if (index == -1) {
            this.values.push({ value, priority });
        } else {
            this.values.splice(index, 0, { value, priority });
        }
    }

    dequeue() {
        return this.values.shift();
    }

    count() {
        return this.values.length;
    }

    isEmpty() {
        return this.count() == 0;
    }
}

export function isDefined<T>(val: T): val is NonNullable<T> {
    return val !== null && val !== undefined;
}

export function humanFileSize(bytes: number, precision = 1) {
    const threshold = 1024;

    if (Math.abs(bytes) < threshold) {
        return bytes + " B";
    }

    const units = ["kB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    let u = -1;
    const r = 10 ** precision;

    do {
        bytes /= threshold;
        ++u;
    } while (Math.round(Math.abs(bytes) * r) / r >= threshold && u < units.length - 1);

    return bytes.toFixed(precision) + " " + units[u];
}

export function sleep(timeout = 1000) {
    return new Promise<void>((resolve) => setTimeout(resolve, timeout));
}

/**
 * Parses "90m", "1h 30m", "2d 4h", or a bare decimal hour count ("1.5") into
 * whole minutes; null when the input isn't a duration.
 */
export function parseDuration(input: string): number | null {
    const trimmed = input.trim().toLowerCase();
    if (trimmed === "") return null;

    if (/^(\d+(\.\d+)?|\.\d+)$/.test(trimmed)) {
        const minutes = Math.round(Number(trimmed) * 60);
        return minutes > 0 ? minutes : null;
    }

    let minutes = 0;
    let consumed = 0;
    for (const match of trimmed.matchAll(/(\d+(?:\.\d+)?|\.\d+)\s*([dhm])/g)) {
        const value = Number(match[1]);
        minutes += match[2] === "d" ? value * 1440 : match[2] === "h" ? value * 60 : value;
        consumed += match[0].replace(/\s+/g, "").length;
    }

    if (consumed === 0 || consumed !== trimmed.replace(/\s+/g, "").length) return null;

    const rounded = Math.round(minutes);
    return rounded > 0 ? rounded : null;
}

/**
 * Duration in seconds from anything a person types into a duration field:
 * "1:15:00" and "1:15" clock forms, "12s" and "4m 12s", plus everything
 * parseDuration takes ("1.25" is hours, "90m", "1h 30m").
 */
export function parseDurationSeconds(input: string): number | null {
    const raw = input.trim().toLowerCase();
    if (raw === "") return null;

    const clock = raw.match(/^(\d+):([0-5]?\d)(?::([0-5]?\d))?$/);
    if (clock) {
        const seconds = Number(clock[1]) * 3600 + Number(clock[2]) * 60 + Number(clock[3] ?? 0);
        return seconds > 0 ? seconds : null;
    }

    const secondsOnly = raw.match(/^(\d+)\s*s$/);
    if (secondsOnly) {
        const seconds = Number(secondsOnly[1]);
        return seconds > 0 ? seconds : null;
    }

    const withSeconds = raw.match(/^(.*?)(?:\s+(\d+)\s*s)$/);
    if (withSeconds) {
        const minutes = parseDuration(withSeconds[1] ?? "");
        return minutes === null ? null : minutes * 60 + Number(withSeconds[2]);
    }

    const minutes = parseDuration(raw);
    return minutes === null ? null : minutes * 60;
}

function clockTimeSeconds(value: string): number | null {
    const match = value.match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
    if (!match) return null;

    return Number(match[1]) * 3600 + Number(match[2]) * 60 + Number(match[3] ?? 0);
}

/**
 * Seconds from one "HH:MM[:SS]" time of day to another. A stop at or before the
 * start is read as the next day, so 22:00 to 01:00 is three hours.
 */
export function secondsBetweenClockTimes(start: string, stop: string): number | null {
    const from = clockTimeSeconds(start);
    const to = clockTimeSeconds(stop);
    if (from === null || to === null) return null;

    const seconds = to - from;
    return seconds > 0 ? seconds : seconds + 86400;
}

/** Duration field text, such as "3h", "2h 30m" or "2h 59m 23s", that parseDurationSeconds reads back exactly. */
export function durationInput(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainder = seconds % 60;
    if (minutes === 0) return `${remainder}s`;

    const whole = humaniseMinutes(minutes);
    return remainder === 0 ? whole : `${whole} ${remainder}s`;
}

/** Formats a duration in hours as "42m", "3h 05m", or "2d 4h". */
export function humanDuration(hours: number): string {
    const totalMinutes = Math.round(hours * 60);
    if (totalMinutes < 60) {
        return `${totalMinutes}m`;
    }

    const totalHours = Math.floor(totalMinutes / 60);
    if (totalHours < 24) {
        return `${totalHours}h ${String(totalMinutes % 60).padStart(2, "0")}m`;
    }

    return `${Math.floor(totalHours / 24)}d ${totalHours % 24}h`;
}

/**
 * Timesheet figure: hours never roll up into days. A logged day is not 24 hours,
 * and concurrent clocks can push even one day past 24, so "3d 4h" would be both
 * wrong and unreadable on a timesheet.
 */
export function hoursDuration(hours: number): string {
    const totalMinutes = Math.max(0, Math.round(hours * 60));

    return `${Math.floor(totalMinutes / 60)}h ${String(totalMinutes % 60).padStart(2, "0")}m`;
}

/** Hours to two places, the way payroll and invoices take time: 1h 30m is "1.50 h". */
export function decimalHours(hours: number): string {
    return `${Math.max(0, hours).toFixed(2)} h`;
}

/** Running-clock face: h:mm:ss, counting hours past a day rather than wrapping. */
export function clockDuration(seconds: number): string {
    const whole = Math.max(0, Math.floor(seconds));
    const minutes = String(Math.floor((whole % 3600) / 60)).padStart(2, "0");

    return `${Math.floor(whole / 3600)}:${minutes}:${String(whole % 60).padStart(2, "0")}`;
}

const shortDurationHumanizer = humanizeDuration.humanizer({
    language: "shortEn",
    languages: { shortEn: { d: () => "d", h: () => "h", m: () => "m" } },
    delimiter: " ",
    spacer: "",
});

const MINUTES_PER_UNIT: Record<"m" | "h" | "d", number> = { m: 1, h: 60, d: 1440 };

/**
 * Formats whole minutes as "42m", "3h 5m" or "2d 4h", clamping negatives to "0m".
 * `coarse` keeps only the leading unit ("3h", "2d") for dense table cells.
 */
export function humaniseMinutes(minutes: number, coarse = false): string {
    const rounded = Math.max(0, Math.round(minutes));
    const units: ("m" | "h" | "d")[] =
        rounded < 60
            ? ["m"]
            : rounded < 1440
              ? coarse
                  ? ["h"]
                  : ["h", "m"]
              : coarse
                ? ["d"]
                : ["d", "h"];

    // The library floors decimals only at render time, printing "1d 0h" for 1441
    // minutes; truncating to the smallest displayed unit up front lets it drop the
    // zero remainder instead.
    const smallest = MINUTES_PER_UNIT[units[units.length - 1] ?? "m"];
    return shortDurationHumanizer((rounded - (rounded % smallest)) * 60_000, { units });
}

export function isTicketKey(val: TicketId | TicketKey): val is TicketKey {
    return /^[A-Z]+-\d+$/.test(val);
}

export function debounce<T extends unknown[], U>(inner: (...params: T) => U, ms = 1000) {
    let timer = 0;
    let resolves: ((v: U) => void)[] = [];

    return function (...params: T) {
        window.clearTimeout(timer);

        timer = window.setTimeout(() => {
            const result = inner(...params);
            resolves.forEach((r) => r(result));
            resolves = [];
        }, ms);

        return new Promise<U>((resolve) => resolves.push(resolve));
    };
}

export function getIcons(version: ProjectVersion) {
    const icons: { icon: string; color: string; title: string }[] = [];

    if (version.releaseDate) {
        if (version.releaseDate < new Date()) {
            // shipped
            icons.push({ icon: "mdi-ferry", color: "green", title: "Released" });
        }
    } else {
        if (version.endDate && version.endDate <= new Date()) {
            // late release
            icons.push({ icon: "mdi-clock-alert", color: "red", title: "Late" });
        } else if (version.endDate) {
            // has an end date that hasn't passed yet
            icons.push({ icon: "mdi-clock", color: "blue", title: "Upcoming" });
        }

        if (version.freezeDate && version.freezeDate < new Date()) {
            // past code freeze
            icons.push({ icon: "mdi-snowflake", color: "light-blue", title: "Frozen" });
        }
    }

    return icons;
}

/**
 * Parses a date-only wire value ("YYYY-MM-DD") as local midnight. `new Date("YYYY-MM-DD")`
 * would parse it as UTC midnight, shifting the calendar day for viewers west of UTC.
 */
export function parseDateOnly(value: string): Date {
    const parts = value.split("-");
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    const day = Number(parts[2]);
    return new Date(year, month - 1, day);
}

/** True for an absolute http or https URL, the same check the server applies to URL fields. */
export function isHttpUrl(value: string): boolean {
    try {
        const url = new URL(value);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch {
        return false;
    }
}

/** Serializes a Date to a date-only wire value ("YYYY-MM-DD") using its local calendar day. */
export function toDateOnly(value: Date): string {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, "0");
    const day = String(value.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

/**
 * Orders versions the way they should read in a list: unreleased first (soonest end date first,
 * undated ones last), then released (most recently released first). Ties break on name - ascending
 * for unreleased (1.9 before 1.10), descending for released so same-day releases still read
 * newest-first (0.3.0 before 0.2.4).
 */
export function compareVersions(a: ProjectVersion, b: ProjectVersion): number {
    const aReleased = a.releaseDate;
    const bReleased = b.releaseDate;

    if (isDefined(aReleased) && isDefined(bReleased)) {
        const byRelease = bReleased.getTime() - aReleased.getTime();
        if (byRelease !== 0) return byRelease;
        return b.name.localeCompare(a.name, undefined, { numeric: true });
    } else if (isDefined(aReleased)) {
        return 1;
    } else if (isDefined(bReleased)) {
        return -1;
    } else {
        const aEnd = a.endDate;
        const bEnd = b.endDate;

        if (isDefined(aEnd) && isDefined(bEnd)) {
            const byEnd = aEnd.getTime() - bEnd.getTime();
            if (byEnd !== 0) return byEnd;
        } else if (isDefined(aEnd)) {
            return -1;
        } else if (isDefined(bEnd)) {
            return 1;
        }
    }

    return a.name.localeCompare(b.name, undefined, { numeric: true });
}

// based on Vuetify's convertToUnit
export function convertToUnit(str: string | number | null, unit = "px") {
    if (str === null || str === "") {
        return undefined;
    } else if (isNaN(+str)) {
        return String(str);
    } else if (!isFinite(+str)) {
        return undefined;
    } else {
        return `${str}${unit}`;
    }
}

export function isItem(v: unknown): v is SelectItem<string> {
    return v instanceof Object && "value" in v && typeof v["value"] === "string";
}

export function arrayEquality(a: unknown[], b: unknown[]): boolean {
    const xordArray = xor(a, b);
    return xordArray.length === 0;
}

export function camelCaseToTitleCase(v: string): string {
    let r = v.replace(
        /^[a-z]|^([A-Z]+)(?=[A-Z]|$)|([A-Z]+)(?=[A-Z]|$)|([A-Z])(?=[a-z]+)/,
        (match: string) => " " + match.toUpperCase()
    );
    if (r[0] == undefined) return "";
    if (r[0] == " ") r = r.slice(1);
    if (r[0] == undefined) return "";
    return r[0].toUpperCase() + r.slice(1);
}

export function humanDate(
    d: Date | string | null,
    formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat("en-US"),
    nullValue: string | null = null
): string | null {
    if (d == null) return nullValue;
    if (typeof d == "string") return formatter.format(new Date(d));
    if (typeof d == "object") return formatter.format(d);
    else return null;
}

export function logicalXOR(lhs: boolean | (() => boolean), rhs: boolean | (() => boolean)) {
    return ((a: boolean, b: boolean) => (a || b) && !(a && b))(
        typeof lhs == "function" ? lhs() : lhs,
        typeof rhs == "function" ? rhs() : rhs
    );
}

// export function enumToSelectItem<ValueType>(
//     enm: { [i: number]: string },
//     zeroIsNull: boolean = false
// ): SelectItem<ValueType | null>[] {
//     const entries = Object.entries(enm)
//         .filter((e) => typeof e[1] == "number")
//         .map((e) => ({
//             title: capitalCase(e[0]),
//             value: e[1] as ValueType,
//             disabled: false,
//         }));

//     return zeroIsNull
//         ? [
//               { title: "Choose One", value: null, disabled: true },
//               ...entries.filter((e) => e.value != 0),
//           ]
//         : entries;
// }

export function enumToSelectItem<T>(
    enm: { [i: string]: number | string },
    zeroIsNull: boolean = false,
    stringValueIsNull?: string
): SelectItem<T | null>[] {
    const entries = Object.entries(enm);
    const last = entries[entries.length - 1];
    if (!last) return [];
    const tp = typeof last[1];
    if (tp == "number") {
        const take = entries.length / 2;
        entries.splice(0, take);
    }

    return [
        ...entries
            .filter((e) =>
                zeroIsNull ? e[1] != 0 : stringValueIsNull ? e[1] != stringValueIsNull : true
            )
            .map((e) => ({
                title: capitalCase(e[0]),
                value: e[1] as T,
                disabled: false,
            })),
    ];
}

export function quickHash(obj: object | null): number {
    const str = JSON.stringify(obj);
    let hash = 0;
    for (let i = 0, len = str.length; i < len; i++) {
        const chr = str.charCodeAt(i);
        hash = (hash << 5) - hash + chr;
        hash |= 0;
    }
    return hash;
}
